// The public landing page at / for signed-out visitors (spec-v2.1 C; v3: every kind of video, spec-v3-formats.md E2).
// The auth gate (D) mounts it:
//   <Landing onSignIn={...} onRequestInvite={...} />
// Self-contained: its own stylesheet (scoped under .lp) and media in public/landing/; fonts are the app's (main.tsx).
// All footage is the app itself, recorded in mock mode (tools/record.mjs); the example ads were cut by
// product/render from code-drawn stills (tools/make-ads.mjs). The "What you can make" showcase (showcase.tsx) reads
// GET /api/formats for samples and falls back to its own list and drawn previews (formats.ts, art.tsx).
import { useEffect, useState } from "react";
import { AD_PRICES, ASSET, FAQ, GALLERY, STEPS, fmt, usd, type GalleryAd } from "./content";
import { BRAND, LandingLogo } from "./brand";
import { IconCheck, IconFilm, IconFrame, IconMic, LoopVideo, PhoneFrame } from "./parts";
import { CARD_PACKS, DIRECTOR_CREDITS_PER_MINUTE, DIRECTOR_TEXT_CREDITS_PER_MINUTE, FEATURED_PACK, STATIC_PACKS, fetchPacks, packLines, usdOf } from "./pricing";
import { Showcase } from "./showcase";
import { ModelShowcase } from "./model-showcase";
import { LEGAL_LINKS } from "../legal/guard";
import "@fontsource-variable/anybody/standard.css"; // the brand's display face today (only ever used through --lp-display)
import "@fontsource-variable/instrument-sans/standard.css"; // brand text
import "@fontsource-variable/martian-mono/standard.css"; // brand timecode
import "../brand/tokens.css"; // the --cao-* roles landing.css reads (main.tsx imports it too once E1 adopts the brand)
import "./landing.css";
import "./showcase.css"; // after landing.css: its reset must not win over the showcase

export type LandingProps = {
  /** Open sign-in (the auth workstream decides which form). */
  onSignIn: () => void;
  /** Start "Request access / enter invite code". */
  onRequestInvite: () => void;
  /** Ask the api for the live format list, samples and credit packs (default true; screenshots can turn it off). */
  liveFormats?: boolean;
};

const STEP_ICON = [IconMic, IconFrame, IconFilm];
const DIMS: Record<GalleryAd["aspect"], [number, number]> = { "9:16": [404, 720], "1:1": [720, 720], "16:9": [960, 540] };

export function Landing({ onSignIn, onRequestInvite, liveFormats = true }: LandingProps) {
  useEffect(() => {
    const prev = document.title;
    document.title = `${BRAND.name}: ${BRAND.descriptor.replace(/\.$/, "").replace(/^The/, "the")}`;
    document.documentElement.classList.add("lp-doc");
    return () => {
      document.title = prev;
      document.documentElement.classList.remove("lp-doc");
    };
  }, []);

  const [packs, setPacks] = useState(STATIC_PACKS);
  useEffect(() => {
    if (!liveFormats) return;
    let on = true;
    void fetchPacks().then((p) => on && p && setPacks(p));
    return () => {
      on = false;
    };
  }, [liveFormats]);
  const cards = CARD_PACKS.map((id) => packs.find((p) => p.id === id)!).filter(Boolean);
  const others = packs.filter((p) => !(CARD_PACKS as readonly string[]).includes(p.id)).sort((a, b) => a.credits - b.credits);

  const Cta = ({ where }: { where: string }) => (
    <div className="lp-cta-row">
      <button type="button" className="lp-btn lp-btn-primary" onClick={onRequestInvite} data-where={where}>
        Request an invite
      </button>
      <button type="button" className="lp-btn lp-btn-ghost" onClick={onSignIn} data-where={where}>
        Sign in
      </button>
    </div>
  );

  return (
    <div className="lp">
      <a className="lp-skip" href="#lp-main">
        Skip to content
      </a>
      <header className="lp-nav">
        <LandingLogo />
        <nav className="lp-nav-links" aria-label="Page sections">
          <a href="#how">How it works</a>
          <a href="#make">Examples</a>
          <a href="#pricing">Pricing</a>
          <a href="#faq">FAQ</a>
        </nav>
        <div className="lp-nav-actions">
          <button type="button" className="lp-link" onClick={onSignIn}>
            Sign in
          </button>
          <button type="button" className="lp-btn lp-btn-primary lp-btn-s" onClick={onRequestInvite}>
            <span className="lp-only-wide">Request an invite</span>
            <span className="lp-only-narrow">Get invited</span>
          </button>
        </div>
      </header>

      <main id="lp-main">
        {/* ---------------------------------------------------------------- hero */}
        <section className="lp-hero" aria-labelledby="lp-h1">
          <div className="lp-hero-copy">
            <p className="lp-eyebrow">{BRAND.tagline.replace(/\.$/, "")}</p>
            <h1 id="lp-h1" className="lp-h1">
              Make any video by talking it through.
            </h1>
            <p className="lp-lede">Ads, documentaries, cartoons, trailers, explainers, Shorts and music videos, made with an AI Director you talk to.</p>
            <Cta where="hero" />
          </div>
          <div className="lp-hero-stage">
            <div className="lp-beam" aria-hidden="true" />
            <PhoneFrame className="lp-hero-phone">
              <LoopVideo eager name="session-phone" width={584} height={1264} label="CAO Studio on a phone: the Director writes the script, then frames and clips appear in the chat for approval" />
            </PhoneFrame>
          </div>
        </section>

        {/* ---------------------------------------------------------------- three steps */}
        <section id="how" className="lp-section lp-steps" aria-labelledby="lp-how">
          <p className="lp-eyebrow">How it works</p>
          <h2 id="lp-how" className="lp-h2">
            Talk. Approve. Done.
          </h2>
          <ol className="lp-step-list">
            {STEPS.map((s, i) => {
              const Icon = STEP_ICON[i];
              return (
                <li key={s.n} className="lp-step">
                  <div className={`lp-step-visual lp-step-visual-${s.n}`}>
                    {s.img ? (
                      <img src={`${ASSET}/${s.img}`} width={720} height={900} loading="lazy" decoding="async" alt={s.n === 1 ? "The chat: you describe the video, the Director answers and shows the script" : "The Needs your review tray: a clip and a frame waiting, each with Approve and Redo"} />
                    ) : (
                      <LoopVideo name="ad-roast" width={404} height={720} label="The finished Morning Roast ad, 15 seconds, 9:16" />
                    )}
                  </div>
                  <div className="lp-step-text">
                    <span className="lp-step-n" aria-hidden="true">
                      <Icon />
                      {s.n}
                    </span>
                    <h3 className="lp-h3">{s.title}</h3>
                    <p>{s.body}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        {/* ---------------------------------------------------------------- what you can make */}
        <Showcase live={liveFormats} />

        {/* ---------------------------------------------------------------- same prompt, every model (V4-MS) */}
        <ModelShowcase live={liveFormats} />

        {/* ---------------------------------------------------------------- gallery */}
        <section id="gallery" className="lp-section lp-gallery" aria-labelledby="lp-gal">
          <p className="lp-eyebrow">Example ads</p>
          <h2 id="lp-gal" className="lp-h2">
            Made in CAO Studio
          </h2>
          <ul className="lp-reel">
            {GALLERY.map((ad) => {
              const [w, h] = DIMS[ad.aspect];
              return (
                <li key={ad.id} className={`lp-reel-item lp-a-${ad.aspect.replace(":", "x")}`}>
                  <LoopVideo name={ad.id} width={w} height={h} label={`${ad.brand}: ${ad.title}, ${ad.seconds} seconds, ${ad.aspect}`} />
                  <p className="lp-reel-cap">
                    <b>{ad.brand}</b>
                    <span>
                      {`${ad.seconds} s`}, {ad.aspect}
                    </span>
                  </p>
                </li>
              );
            })}
          </ul>
        </section>

        {/* ---------------------------------------------------------------- pricing */}
        <section id="pricing" className="lp-section lp-pricing" aria-labelledby="lp-price">
          <p className="lp-eyebrow">Pricing, invite-only beta</p>
          <h2 id="lp-price" className="lp-h2">
            Pay once. See every price first.
          </h2>
          <div className="lp-plans">
            {cards.map((p) => {
              const featured = p.id === FEATURED_PACK;
              return (
                <article key={p.id} className={`lp-plan ${featured ? "is-featured" : ""}`} aria-labelledby={`lp-plan-${p.id}`}>
                  <h3 id={`lp-plan-${p.id}`} className="lp-h3">
                    {p.name}
                  </h3>
                  <p className="lp-plan-price">
                    <b>{usdOf(p.usdCents)}</b>
                    <span>once</span>
                  </p>
                  <p className="lp-plan-credits">{fmt(p.credits)} credits</p>
                  <ul>
                    {packLines(p).map((l) => (
                      <li key={l}>
                        <IconCheck />
                        {l}
                      </li>
                    ))}
                  </ul>
                  <button type="button" className={`lp-btn ${featured ? "lp-btn-primary" : "lp-btn-ghost"}`} onClick={onRequestInvite}>
                    Request an invite
                  </button>
                </article>
              );
            })}
            <aside className="lp-costs" aria-labelledby="lp-costs-h">
              <h3 id="lp-costs-h" className="lp-h4">
                What a 30-second video costs
              </h3>
              <dl>
                {AD_PRICES.map((a) => (
                  <div key={a.what}>
                    <dt>{a.what}</dt>
                    <dd>
                      <b>{usd(a.credits)}</b> <span>{fmt(a.credits)} credits</span>
                    </dd>
                  </div>
                ))}
                <div>
                  <dt>Talking with the Director</dt>
                  <dd>
                    <b>{fmt(DIRECTOR_CREDITS_PER_MINUTE)}</b> <span>credits a minute</span>
                  </dd>
                </div>
                <div>
                  <dt>Typing to the Director</dt>
                  <dd>
                    <b>{fmt(DIRECTOR_TEXT_CREDITS_PER_MINUTE)}</b> <span>credits a minute</span>
                  </dd>
                </div>
              </dl>
              <p>A credit is about a cent. Failed work is refunded.</p>
            </aside>
          </div>
          {others.length > 0 && (
            <div className="lp-packs">
              <h3 className="lp-h4">Other credit packs</h3>
              <ul>
                {others.map((p) => (
                  <li key={p.id}>
                    <b>{fmt(p.credits)} credits</b> <span>{usdOf(p.usdCents)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* ---------------------------------------------------------------- FAQ */}
        <section id="faq" className="lp-section lp-faq" aria-labelledby="lp-faq">
          <p className="lp-eyebrow">Questions</p>
          <h2 id="lp-faq" className="lp-h2">
            Good to know
          </h2>
          <div className="lp-faq-list">
            {FAQ.map((f) => (
              <details key={f.q}>
                <summary>
                  <h3 className="lp-h4">{f.q}</h3>
                </summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------------- closing CTA */}
        <section className="lp-closer" aria-labelledby="lp-close">
          <div className="lp-beam lp-beam-closer" aria-hidden="true" />
          <h2 id="lp-close" className="lp-h1 lp-closer-h">
            What will you make?
          </h2>
          <Cta where="closer" />
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-footer-top">
          <LandingLogo />
          <div className="lp-footer-links">
            <button type="button" className="lp-link" onClick={onSignIn}>
              Sign in
            </button>
            <button type="button" className="lp-link" onClick={onRequestInvite}>
              Request an invite
            </button>
            {LEGAL_LINKS.map((l) => (
              <a key={l.page} className="lp-link" href={l.path}>
                {l.short}
              </a>
            ))}
          </div>
        </div>
        <p className="lp-footer-note">Footage is CAO Studio in demo mode. The brands in the example ads are made up.</p>
        <p className="lp-footer-note">© {new Date().getFullYear()} CAO Studio</p>
      </footer>
    </div>
  );
}
